<template>
  <div>
    <hi-search placeholder="Find your shares" v-model="searchFor" />

    <my-filter
      @filterChanged="filterChanged"
      @sortOn="sortOn"
      :hide-language="true"
      :hide-keyword="true"
      :hide-sort="true"
    />

    <!-- bulk delete -->
    <div v-if="selected.length" class="row q-py-md justify-between">
      <q-btn color="warning" no-caps @click="deleteShares">
        Delete {{ selected.length }} selected
        {{ selected.length === 1 ? "share" : "shares" }}
      </q-btn>
    </div>

    <q-table
      flat
      dense
      separator="cell"
      row-key="uuid"
      selection="multiple"
      :selected.sync="selected"
      :data="normalizedShares"
      :columns="columns"
      :rows-per-page-options="[100]"
    >
      <!-- Title with image -->
      <!-- Title column: image + title only -->
      <template v-slot:body-cell-title="p">
        <q-td class="q-td--wrap text-wrap">
          <!-- <img v-if="p.row.hasImage" :src="p.row.image" class="thumb" /> -->
          {{ p.row.title }}
        </q-td>
      </template>

      <!-- Short link column -->
      <template v-slot:body-cell-short="props">
        <q-td :props="props">
          <a :href="props.row.url" target="_blank">{{ props.row.short }}</a>
          <q-btn
            flat
            dense
            round
            size="sm"
            icon="far fa-copy"
            class="q-ml-sm"
            @click="copyToClipboard(props.row.url)"
          />
        </q-td>
      </template>

      <template v-slot:body-cell-created="props">
        <q-td :props="props">
          {{ props.row.created.toLocaleDateString("en-GB") }}
        </q-td>
      </template>

      <!-- Answers column -->
      <template v-slot:body-cell-answers="p">
        <q-td>
          <div v-if="p.row.answers" class="row items-center justify-end">
            {{ p.row.submitted }} / {{ p.row.answers }}
            <q-btn
              flat
              dense
              round
              size="sm"
              icon="info"
              @click="showStatus($event, p.row.uuid)"
            />
          </div>
          <span v-else>–</span>
        </q-td>
      </template>

      <!-- Review button column -->
      <template v-slot:body-cell-review="p">
        <q-td>
          <q-btn
            v-if="p.row.answers"
            dense
            no-caps
            color="primary"
            icon="far fa-tasks"
            label="Review"
            :href="`/ea/review/${p.row.uuid}`"
            target="_blank"
            xx
          />
        </q-td>
      </template>
    </q-table>

    <!-- student menu -->
    <q-menu
      ref="studentMenu"
      :target="statusElm"
      anchor="top middle"
      self="bottom middle"
    >
      <q-list>
        <q-item
          v-for="a in students"
          :key="a.uuid"
          clickable
          v-close-popup
          @click="copyToClipboard(a.member.email)"
        >
          <q-item-section>
            <div class="row items-center justify-between">
              {{ a.member.email }}
              <q-icon v-if="a.submitted" name="check" color="green" />
            </div>
          </q-item-section>
        </q-item>
      </q-list>
    </q-menu>
  </div>
</template>

<script>
import tic from "../tic";
import { API } from "../tic";
import MyFilter from "../components/MyFilter";
import clipboard from "../mixins/clipboard.mixin.js";
import { mapGetters } from "vuex";

export default {
  name: "MyShares",
  components: { MyFilter },
  mixins: [clipboard],

  data() {
    return {
      shares: [],
      selected: [],
      students: [],
      statusElm: null,
      searchFor: null,
      activeFilters: [],

      columns: [
        {
          name: "title",
          label: "e-Activity",
          field: "title",
          align: "left",
          sortable: true,
        },
        { name: "short", label: "Short link", field: "short" },
        {
          name: "created",
          label: "Date shared",
          field: "created",
          sortable: true,
          // sortMethod: (a, b) => {
          //   // parse date strings to Date objects
          //   const da = new Date(a.split("/").reverse().join("/")); // DD/MM/YYYY → YYYY/MM/DD
          //   const db = new Date(b.split("/").reverse().join("/"));
          //   return da - db; // ascending
          // },
          sortOrder: "ad",
          format: (value) => new Date(value).toLocaleDateString("en-GB"),
        },
        { name: "mode", label: "Shared with", field: "mode" },
        {
          name: "class",
          label: "Class",
          field: "tags",
          format: (tags) => {
            return (tags || [])
              .filter((t) => t.label === "MyClasses")
              .map((t) => t.name)
              .join(", ");
          },
        },
        {
          name: "year",
          label: "Year",
          field: "tags",
          format: (tags) => {
            return (tags || [])
              .filter((t) => t.label === "MyYears")
              .map((t) => t.name)
              .join(", ");
          },
        },
        { name: "answers", label: "Answers" },
        { name: "review", label: "Review" },
      ],
    };
  },

  computed: {
    ...mapGetters("user", ["loginUuid"]),

    filteredShares() {
      let r = [...this.shares];

      // Search filter
      if (this.searchFor) {
        const q = this.searchFor.toLowerCase();
        r = r.filter((s) => s.title.toLowerCase().includes(q));
      }

      // Active filters
      if (this.activeFilters.length > 0) {
        r = r.filter((s) => {
          // Each active filter must match at least one tag in the share
          return this.activeFilters.every((f) =>
            (s.tags || []).some((t) => t.uuid === f.uuid)
          );
        });
      }

      return r;
    },

    normalizedShares() {
      return this.filteredShares.map((s, i) => ({
        uuid: s.uuid,
        title: s.title || "",
        short: s.short?.split("/").pop() || "",
        url: s.short,
        created: new Date(s.share?.created),
        mode: s.share.mode || "",
        hasImage: s.hasImage || false,
        image: s.image || "",
        years: s.years || [],
        classes: s.classes || [],
        submitted: s.submitted || 0,
        answers: s.answers || 0,
        tags: s.tags || [],
      }));
    },
  },

  mounted() {
    this.getShares();
    API.post("/getProfile", { uuid: this.loginUuid }).then((response) => {
      const tags = response.data.user?.tags || [];
      const tagLookup = {
        MyLanguages: this.languages,
        MyYears: this.years,
        MyClasses: this.classes,
        MyKeywords: this.keywords,
      };
      for (let t of tags) {
        if (tagLookup[t.tag]) tagLookup[t.tag].push(t);
      }
    });
  },

  methods: {
    getShares() {
      API.post("/my/shares", { member: this.loginUuid }).then((response) => {
        console.log("◆ API /my/shares", response);
        this.shares = response.data.shares || [];
      });
    },

    created() {
      this.getShares();
    },

    showStatus(e, uuid) {
      this.statusElm = e.currentTarget;
      API.post("/my/shares/status", {
        owner: this.loginUuid,
        share: uuid,
      }).then((r) => {
        this.students = r.data.data;
        this.$refs.studentMenu.show();
      });
    },

    deleteShares() {
      API.post("/my/shares/delete", {
        member: this.loginUuid,
        uuids: this.selected.map((s) => s.uuid),
      }).then(() => {
        this.selected = [];
        this.getShares();
      });
    },

    filterChanged(f) {
      this.activeFilters = f;
    },
    sortOn() {},
  },
};
</script>

<style scoped>
.thumb {
  width: 50px;
  height: 50px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
